import { useEffect, useState } from "react";
import axios from "axios";
import { X, ArrowUpRight, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) =>
  Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const UpgradeModal = ({ token, onDismissed }) => {
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/terminal/upgrade-notice`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data.pending) setNotice(data.notice);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [token]);

  const dismiss = async () => {
    if (!notice) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/terminal/upgrade-notice/ack`,
        { id: notice.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotice(null);
      onDismissed?.();
    } catch {
      setNotice(null);
    } finally {
      setBusy(false);
    }
  };

  if (!notice) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/90 p-4 backdrop-blur-sm"
      data-testid="upgrade-modal"
    >
      <div className="relative w-full max-w-md overflow-hidden border border-gold/30 bg-surface shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
        <button
          onClick={dismiss}
          disabled={busy}
          className="absolute right-4 top-4 text-white/40 transition-colors hover:text-white disabled:opacity-40"
          data-testid="upgrade-modal-close"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="bg-gradient-to-br from-gold/20 via-transparent to-transparent p-6 pb-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-gold" />
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
              Upgrade confirmed
            </p>
          </div>
          <h2 className="mt-3 text-2xl font-semibold text-white">
            You're on <span className="text-gold">{notice.new_tier_name}</span>
            <span className="text-white/40"> · </span>
            <span className="font-tabular">${fmt(notice.new_tier_amount)}</span>
          </h2>
          <p className="mt-2 flex items-center gap-1 text-[11px] text-white/55">
            <span className="font-tabular text-white/40 line-through">{notice.old_tier_name}</span>
            <ArrowUpRight className="h-3 w-3 text-gold" />
            <span className="font-tabular text-gold">{notice.new_tier_name}</span>
            <span className="ml-2 text-white/40">+${fmt(notice.delta)} credited</span>
          </p>
        </div>

        <div className="space-y-3 border-t border-white/10 p-6">
          <p className="text-[11px] leading-relaxed text-white/60">
            Here's exactly what sits in your account right now — so you know what
            is <span className="text-gold">locked seed capital</span> and what is{" "}
            <span className="text-[#26A69A]">withdrawable profit</span>.
          </p>

          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="border border-white/10 bg-ink/60 p-4" data-testid="upgrade-modal-seed">
              <p className="text-[9px] uppercase tracking-[0.25em] text-white/40">New seed (locked)</p>
              <p className="mt-1 font-tabular text-xl text-gold">${fmt(notice.new_seed)}</p>
              <p className="mt-1 text-[10px] text-white/40">
                Up from ${fmt(notice.old_seed)}
              </p>
            </div>
            <div className="border border-[#26A69A]/25 bg-ink/60 p-4" data-testid="upgrade-modal-withdrawable">
              <p className="text-[9px] uppercase tracking-[0.25em] text-[#26A69A]">Withdrawable profit</p>
              <p className="mt-1 font-tabular text-xl text-[#26A69A]">${fmt(notice.withdrawable_after)}</p>
              <p className="mt-1 text-[10px] text-white/40">
                Balance ${fmt(notice.balance_after)}
              </p>
            </div>
          </div>

          <div className="rounded-sm border border-gold/20 bg-gold/[0.04] p-3 text-[10px] leading-relaxed text-gold/80">
            <span className="font-semibold uppercase tracking-[0.15em]">Reminder</span> — The
            amount you just paid is your <span className="text-gold">new tier seed</span>, not
            spendable profit. Only profit earned <em>above</em> this seed (from daily boost +
            trading) can be withdrawn. Your next payout window opens in 14 days.
          </div>

          <button
            onClick={dismiss}
            disabled={busy}
            data-testid="upgrade-modal-ack"
            className="mt-2 w-full rounded-full bg-gold px-6 py-3 text-[11px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-gold-light disabled:opacity-40"
          >
            {busy ? "Confirming…" : "Got it · Start trading"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
